import { t } from '@lingui/core/macro';
import type { BookingGarageRecordCreate, PaymentOption } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { noddiToast } from 'noddi-ui-web';
import { customerRoutes, getErrorRedirectUrl, hasErrorCode } from 'noddi-util';
import { commonTrackingEvents, toFailureProps, tracking, type BookingOrderMoney } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { setProposalPayBeforeCheckoutInFlight } from '../utils/proposalPayBeforeCheckout';
import { useCustomerDepartmentSelection } from './useCustomerDepartmentSelection';
import { useUserGroupSelection } from './useUserGroupSelection';

type CreateStationaryBookingBody = BookingGarageRecordCreate;

type Options = {
  onBookingCreated?: (data: {
    slug: string;
    paymentMethod: PaymentOption;
    bookingId?: number;
    order?: BookingOrderMoney | null;
  }) => void;
};

type CreateOptions = {
  shouldPayBefore: boolean;
  paymentMethod: PaymentOption;
};

/**
 * Shared create-at-station + optional Dintero session navigation.
 * Used by the stationary booking flow and stationary proposal confirm.
 *
 * Post-create navigation stays in the call stack (no pending-options ref).
 * `booking_exists_for_season` is surfaced via the exists-banner (no auto-redirect).
 * `shouldPayBefore` is per-call so invoice orgs can optionally pay immediately.
 *
 * The booking is already created once the payment session is requested, so a
 * failed session must never strand the user on the confirm step: land on the
 * confirmation page (where payment can be retried) and say what happened.
 */
export function useCreateStationaryBookingWithPayment({ onBookingCreated }: Options) {
  const navigate = useNavigate();
  const { selectUserGroup } = useUserGroupSelection();
  const { getSelectedDepartmentForGroup } = useCustomerDepartmentSelection();

  const { mutateAsync: createDinteroSession, isPending: isCreateDinteroSessionLoading } = noddiAsync.usePost({
    type: URLKeys.postDinteroSession
  });

  const {
    mutateAsync: createStationaryBookingMutation,
    isPending: isCreateBookingLoading,
    error: createBookingError
  } = noddiAsync.usePost({
    type: URLKeys.postCreateBookingAtStationIgnoringImpersonation,
    queryConfig: {
      onError: (error) => {
        tracking.track(commonTrackingEvents.bookingFailed, toFailureProps(error, 'create_stationary_booking'));
      }
    }
  });

  const isBookingExistsError = createBookingError && hasErrorCode(createBookingError, 'booking_exists_for_season');
  const existingBookingUrl = createBookingError ? getErrorRedirectUrl(createBookingError) : null;
  const isPending = isCreateBookingLoading || isCreateDinteroSessionLoading;

  return {
    createStationaryBooking: async (body: CreateStationaryBookingBody, options: CreateOptions) => {
      // Creating the booking closes a linked proposal before payment is ready.
      // Keep the proposal slug wrapper from painting the "already booked" screen.
      if (options.shouldPayBefore) {
        setProposalPayBeforeCheckoutInFlight(true);
      }

      try {
        const { data } = await createStationaryBookingMutation({
          ...body,
          customerDepartmentId: getSelectedDepartmentForGroup(body.userGroupId)?.id
        });

        // Mark stale without refetching: the only mounted copy of this query is the
        // summary's existing-booking probe, which is about to unmount, and the bookings
        // page refetches on mount anyway. A plain invalidate fired a request here.
        // eslint-disable-next-line noddi/require-invalidate-in-query-config -- Legacy payment flow; preserve stale-only invalidation and proposal/navigation timing during lint rollout.
        void noddiAsync.queryClient.invalidateQueries({
          queryKey: [URLKeys.getPaginatedBookingsForCustomer],
          refetchType: 'none'
        });

        const order = data.order;
        onBookingCreated?.({
          slug: data.slug,
          paymentMethod: options.paymentMethod,
          bookingId: data.id,
          order
        });

        const confirmationPath = customerRoutes.confirmation.getPath({ slug: data.slug });

        // Selecting the group rewrites currentUserGroupId, which is part of the summary's
        // query keys — keep it in the same tick as navigate so the unmounting page cannot
        // re-render and refetch orgs-for-car and bookings-for-customer under new keys.
        const selectGroupAndNavigate = (to: string) => {
          // selectUserGroup ignores a group the user is not in, such as an
          // organization they booked on without being a member.
          selectUserGroup(body.userGroupId, { keepBookingDrafts: true });
          navigate(to, { replace: true });
        };

        if (options.shouldPayBefore && order && order.amountGross.amount > 0) {
          // Stop the slug query from 410ing (and logging) while Dintero starts.
          await noddiAsync.queryClient.cancelQueries({ queryKey: [URLKeys.getBookingProposalBySlug] });

          try {
            const session = await createDinteroSession({ orderId: order.id });
            selectGroupAndNavigate(
              customerRoutes.payForBooking.getPath({
                sessionId: session.data.sessionId,
                countryCode: session.data.countryCode
              })
            );
          } catch {
            // A pay-before booking is a draft until it is paid, so never send the
            // customer to the confirmation screen here — the booking page states
            // that payment is still required and offers the pay CTA.
            noddiToast.error(
              t`Your time slot is reserved, but we could not start the payment. Your booking is not confirmed until it is paid.`
            );
            selectGroupAndNavigate(customerRoutes.bookingInfo.getPath({ slug: data.slug }));
          }
        } else {
          selectGroupAndNavigate(confirmationPath);
        }

        return data;
      } finally {
        if (options.shouldPayBefore) {
          // Clear after navigate so the proposal tree cannot paint 410 in the same tick.
          queueMicrotask(() => setProposalPayBeforeCheckoutInFlight(false));
        }
      }
    },
    isPending,
    createBookingError,
    isBookingExistsError,
    existingBookingUrl
  };
}
